import { apiClient } from '@/lib/generated/api-client'
import type { components } from '@/lib/generated/api-types'
import { useAuthStore } from '@/stores/authStore'
import type { UserGroup } from '@/stores/types'
// debito tecnico: rumuovere commenti di LLM, segnalare errori all'User in qualche modo
// ripulire i tipi (ci sono cast as any inutili)
export type LoginMessage = components['schemas']['LoginMessage']
export type Login = components['schemas']['Login']

export interface SiweAuthConfig {
	domain: string
	uri: string
	statement: string
	version: string
	chain_id: number
}

export interface SiweAuthResult {
	success: boolean
	user?: {
		roles: string[] // Deprecated: use groups instead
		groups: UserGroup[] // New: complete group objects with id and name
		email?: string
		username?: string
		walletAddress?: string
		smartAccountAddress?: string
	}
	accessToken?: string
	refreshToken?: string
	error?: string
}

/**
 * Creates a SIWE message for wallet authentication
 */
export function createSiweMessage(
	config: SiweAuthConfig,
	address: string,
	nonce: string,
	issued_at: string,
): LoginMessage {
	return {
		domain: config.domain,
		uri: config.uri,
		address,
		statement: config.statement,
		version: config.version,
		chain_id: config.chain_id,
		nonce,
		issued_at,
	}
}

export function createSiwxMessage(
	config: SiweAuthConfig,
	userId: string,
	nonce: string,
	issuedAt: string,
): LoginMessage {
	return {
		domain: config.domain,
		uri: config.uri,
		address: userId, // For SIWX, we use the user ID as address
		statement: config.statement,
		version: config.version,
		chain_id: config.chain_id,
		nonce,
		issued_at: issuedAt,
	}
}

export async function getSiweNonce(): Promise<string> {
	try {
		const response = await apiClient.POST('/api/v1/siwe/nonce/')

		if (response.error) {
			throw new Error('Failed to get nonce')
		}

		return response.data?.nonce || Math.random().toString(36).substring(2, 15)
	} catch (error) {
		console.error('Error getting nonce:', error)
		throw new Error('Failed to get nonce')
	}
}

/**
 * Get verify SIWE from the server
 */
export async function getSiweVerify(): Promise<{
	success: boolean
	message: string
}> {
	try {
		const response = await apiClient.POST('/api/v1/siwe/verify/', {
			body: {
				token: useAuthStore.getState().auth.accessToken || '',
				valid: true, //included for sake of api-generator
			},
		})

		if (response.error) {
			throw new Error('Failed to verify')
		}

		return {
			success: (response.data as any)?.success || false,
			message: (response.data as any)?.message || 'Verification failed',
		}
	} catch (error) {
		console.error('Error getting verify:', error)
		throw new Error('Failed to verify session')
	}
}

/**
 * Detect signature type (EOA vs Smart Contract Wallet)
 */
export function detectSignatureType(signature: string): 'eoa' | 'smart_contract' {
	// Smart contract wallet signatures typically start with 0x000000000000000000000000...
	if (signature.startsWith('0x000000000000000000000000') && signature.length > 200) {
		return 'smart_contract'
	}
	return 'eoa'
}

/**
 * Authenticate with SIWE message and signature
 */
export async function authenticateWithSiwe(
	message: LoginMessage,
	signature: string,
): Promise<SiweAuthResult> {
	try {
		const response = await apiClient.POST('/api/v1/siwe/login/', {
			body: {
				message: message,
				signature: signature, // Signature as string
				access: '',
				refresh: '',
			},
		})

		if (response.error || !response.data) {
			return {
				success: false,
				error: 'Authentication failed',
			}
		}

		const { access, refresh } = response.data

		// Store tokens
		useAuthStore.getState().auth.setTokens({ accessToken: access, refreshToken: refresh })

		// Get user data after successful authentication
		const userResponse = await apiClient.GET('/api/v1/siwe/wallet/me/')

		if (userResponse.error) {
			return {
				success: false,
				error: 'Failed to get user data',
			}
		}

		// Extract groups from the response
		const groups = (userResponse.data as any)?.wallet?.groups || []
		const roles = groups.map((group: UserGroup) => group.name) // Extract role names for backward compatibility

		return {
			success: true,
			user: {
				roles, // Backward compatibility
				groups, // Complete group objects
				email: (userResponse.data as any)?.email,
				username: (userResponse.data as any)?.username,
				walletAddress: (userResponse.data as any)?.wallet?.wallet_address,
				smartAccountAddress: (userResponse.data as any)?.wallet?.smart_account_address,
			},
			accessToken: access,
			refreshToken: refresh,
		}
	} catch (error) {
		console.error('SIWE authentication error:', error)
		return {
			success: false,
			error: 'Authentication failed',
		}
	}
}

/**
 * Refresh the SIWE session
 */
export async function refreshSiweSession(): Promise<boolean> {
	try {
		const { refreshToken, setTokens } = useAuthStore.getState().auth

		if (!refreshToken) {
			console.error('No refresh token available')
			return false
		}

		const response = await apiClient.POST('/api/v1/siwe/refresh/', {
			body: {
				refresh: refreshToken,
				access: '', // This field is not required, as it is supposed to be response-only, but included for sake of api-generator
				valid: true, // This field is not required, as it is supposed to be response-only, but included for sake of api-generator
			},
		})

		if (response.error || !response.data) {
			console.error('Refresh token request failed:', response.error)
			return false
		}

		const { access, refresh } = response.data

		// Update tokens in the store
		setTokens({
			accessToken: access,
			refreshToken: refresh || refreshToken,
		})

		return true
	} catch (error) {
		console.error('Error refreshing session:', error)
		return false
	}
}

/**
 * Verify the SIWE session
 */
export async function verifySiweSession(): Promise<boolean> {
	try {
		const response = await apiClient.POST('/api/v1/siwe/verify/', {
			body: {
				token: useAuthStore.getState().auth.accessToken || '',
				valid: true, // This field is not required, as it is supposed to be response-only, but included for sake of api-generator
			},
		})

		if (response.error) {
			return false
		}

		return true
	} catch (error) {
		console.error('Error verifying session:', error)
		return false
	}
}

/**
 * Get user data from SIWE session
 */
export async function getSiweUserData(): Promise<{
	roles: string[] // Deprecated: use groups instead
	groups: UserGroup[] // New: complete group objects with id and name
	email?: string
	username?: string
	walletAddress?: string
	smartAccountAddress?: string
} | null> {
	try {
		const response = await apiClient.GET('/api/v1/siwe/wallet/me/')

		if (response.error) {
			return null
		}

		// Extract groups from the response
		const groups = (response.data as any)?.wallet?.groups || []
		const roles = groups.map((group: UserGroup) => group.name) // Extract role names for backward compatibility

		return {
			roles, // Backward compatibility
			groups, // Complete group objects
			email: (response.data as any)?.email,
			username: (response.data as any)?.username,
			walletAddress: (response.data as any)?.wallet?.wallet_address,
			smartAccountAddress: (response.data as any)?.wallet?.smart_account_address,
		}
	} catch (error) {
		console.error('Error getting user data:', error)
		return null
	}
}
